import { StorefrontThemePageSchema, type ElementTreeDto } from '@xshop/shared';
import type { PageType, ThemeDirection, ThemeKind, ThemeStyles } from './registry';

/**
 * One page of a theme as the server resolved it for this visit.
 *
 * A shopper receives the palette and the single page they are looking at, with the merchant's edits
 * already merged in — never the theme's other pages, which the browser cannot draw and which are
 * megabytes of a catalogue theme's copy. The mirror of `StorefrontThemePageSchema` in
 * `backend/packages/shared/src/schemas/theme.schema.ts`, which is what validates the values.
 */
export type StorefrontThemePage = {
  slug: string;
  name: string;
  kind: ThemeKind;
  direction: ThemeDirection;
  /** The page that was actually resolved: the requested one, unless the theme does not ship it. */
  page: PageType;
  /** Every page type this theme ships, without their trees. */
  pages: readonly PageType[];
  styles: ThemeStyles;
  /** The page itself. Empty roots is a page the merchant has not built yet, never a missing answer. */
  tree: ElementTreeDto;
  /** The store's own stylesheet, when it has one. */
  customCss?: string;
};

// Validates the server's page-scoped theme payload at the edge, answering null for anything that is not one.
export function readStorefrontTheme(raw: unknown): StorefrontThemePage | null {
  // Stripped rather than strict, so a field a newer server adds never blanks a page this bundle could draw.
  const parsed = StorefrontThemePageSchema.strip().safeParse(raw);
  // Zod widens every literal to `string`, so this cast is the boundary between the wire and the named
  // type; what actually checked the colours, lengths and element ids is the schema that just ran.
  return parsed.success ? (parsed.data as unknown as StorefrontThemePage) : null;
}
